import React, { useState } from "react";
import { connect, useDispatch } from "react-redux";
import {
  SET_PLAN_PERIOD_TYPE,
  SET_USER_AND_PLAN_DETAILS,
  SET_IS_LOADING,
} from "../../../actions/types";
import IdeaIcon from "../../../assets/icons/idea_icon.svg";
import {
  Row,
  Col
} from "reactstrap";
import ToolTip from "../../events/Components/Common/ToolTip";
import ConfirmPaymentModal from "./confirm-payment-modal";
import CancelPlanModal from "./cancel-plan-modal";
import PlanDetailModal from "./plan-detail-modal";
import isEmpty from "../../../validation/is-empty";
import CardInfoModal from "./card-info-modal";
import { notification } from "../../../utils/utility";
import axiosInstance from "../../../axios-instance";
import Loader from "../../../components/Loader/Loader";
import UpdateCardModal from "./update-card-modal";
import { getPlanDisplayPrice } from "../../../utils/planLimits";
import { isTrialEligible, isTrialingPlan } from "../../../utils/trialAccess";
import { fetchUserFeatures } from "../../../actions/featureAccessActions";

const normalizePeriod = (value) =>
  value === "annual" || value === "annually" || value === "yearly"
    ? "annual"
    : "monthly";

const SubscriptionPlans = (props) => {
  const dispatch = useDispatch();

  const [showCardInfoModal, setShowCardInfoModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingSentence, setLoadingSentence] = useState(null);
  const [showChangeCardModal, setShowChangeCardModal] = useState(false);
  const { plans, planPeriodType, userPlan } = props.subscription;

  /**
   * on cancel plan
   * @param {*} reason
   * @param {*} natureOfIssue
   * @param {*} preferredContact
   */
  const onCancelSubscription = async (reason, natureOfIssue, preferredContact) => {
    const { getUserAndPlans } = props;

    dispatch({ type: SET_IS_LOADING, payload: true });

    let res;
    try {
      res = await axiosInstance.post("/admin/subscription/cancel", {});
    } catch (err) {
      dispatch({ type: SET_IS_LOADING, payload: false });
      notification.error(
        err?.response?.data?.message || "An error occurred while cancelling. Please try again."
      );
      return;
    }

    dispatch({ type: SET_IS_LOADING, payload: false });

    if (res.data.success) {
      notification.success(res.data.message);
      setLoadingSentence("Cancelling your subscription…");
      setIsLoading(true);
      setTimeout(() => {
        getUserAndPlans();
        dispatch(fetchUserFeatures());
        setIsLoading(false);
      }, 1500);
    } else {
      notification.error(res.data.message || "Cancellation failed. Please try again.");
    }
  };

  /**
   * upgrade plan handler
   * @param {*} data
   */
  const upgradePlanHandler = async (data) => {
    const { getUserAndPlans } = props;
    const { selectedPlan, planPeriodType } = props.subscription;

    dispatch({ type: SET_IS_LOADING, payload: true });

    let res;
    try {
      res = await axiosInstance.post("/admin/subscription/update", {
        plan_id:   selectedPlan.id,
        plan_type: planPeriodType,
      });
    } catch (err) {
      dispatch({ type: SET_IS_LOADING, payload: false });
      notification.error(
        err?.response?.data?.message || "An error occurred while updating your plan. Please try again."
      );
      return;
    }

    dispatch({ type: SET_IS_LOADING, payload: false });

    if (res.data.success) {
      notification.success(res.data.message);
      setLoadingSentence(
        res.data.is_downgrade
          ? "Scheduling your plan change…"
          : "Updating your subscription…"
      );
      setIsLoading(true);
      setTimeout(() => {
        getUserAndPlans();
        dispatch(fetchUserFeatures());
        setIsLoading(false);
      }, 1500);
    } else {
      notification.error(res.data.message || "Plan update failed. Please try again.");
    }
  };

  /**
   * on upgrade plan, in case you are moving from free plan to paid
   * @param {*} plan
   */
  const onUpgradePlan = (plan) => {
    setShowCardInfoModal(true);
    dispatch({
      type: SET_USER_AND_PLAN_DETAILS,
      payload: {
        selectedPlan: plan,
      },
    });
  };

  // Flat list of active plans (deactivated plans are already filtered out by the API)
  const displayPlans = [...(plans || [])].sort(
    (a, b) => Number(a.monthly_price) - Number(b.monthly_price)
  );

  // Calculate annual savings percentage from the cheapest paid plan
  const annualSavingsPct = (() => {
    const paid = displayPlans.find((p) => Number(p.monthly_price) > 0);
    if (!paid || !Number(paid.monthly_price) || !Number(paid.annual_price)) {
      return 20;
    }
    const monthlyTotal = Number(paid.monthly_price) * 12;
    const saving = monthlyTotal - Number(paid.annual_price);
    return Math.round((saving / monthlyTotal) * 100);
  })();

  return (
    <>
      {/* Monthly Anullay Switch */}
      <Loader isShowLoader={isLoading} loadingText={loadingSentence} />
      {showChangeCardModal && <UpdateCardModal toggle={setShowChangeCardModal}/>}
      <div className="mb-4 d-flex flex-wrap justify-content-center align-items-center gap-3">
        <div className="billing-period-toggle" role="group" aria-label="Billing period">
          <button
            type="button"
            className={`billing-period-toggle__btn ${
              props.subscription.planPeriodType === "monthly" ? "active" : ""
            }`}
            onClick={() =>
              dispatch({ type: SET_PLAN_PERIOD_TYPE, payload: "monthly" })
            }
          >
            Monthly
          </button>
          <button
            type="button"
            className={`billing-period-toggle__btn ${
              props.subscription.planPeriodType === "annual" ? "active" : ""
            }`}
            onClick={() =>
              dispatch({ type: SET_PLAN_PERIOD_TYPE, payload: "annual" })
            }
          >
            Annually
          </button>
        </div>
        {userPlan.is_paid_subscription === "E" && (
          <button
            type="button"
            className="btn btn_success btn-14"
            style={{ width: "auto" }}
            onClick={() => setShowChangeCardModal(true)}
          >
            Change Card
          </button>
        )}
        {userPlan.is_paid_subscription === "E" && (
          <CancelPlanModal cancelPlan={onCancelSubscription} />
        )}
      </div>
      {/* Annual Subscription Benefit */}
      {userPlan.pending_change_at && (
        <div className="cmplt_prof_msg subscription_msg">
          <img src={IdeaIcon} alt="Alert Icon" />{" "}
          <p>
            You’ll keep your current plan until{" "}
            <span>
              {new Date(userPlan.pending_change_at).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </span>
            . You’ll move to {userPlan.pending_plan_name || "the selected plan"} then.
          </p>
        </div>
      )}
      {props.subscription.planPeriodType === "monthly" && (
        <div className="cmplt_prof_msg subscription_msg">
          <img src={IdeaIcon} alt="Alert Icon" />{" "}
          <p>
            Switch to annual billing and save <span>{annualSavingsPct}%</span>
          </p>
        </div>
      )}
      <Row className="pt-2 g-4">
        {showCardInfoModal && (
          <CardInfoModal
            getUserAndPlans={props.getUserAndPlans}
            setShowCardInfoModal={setShowCardInfoModal}
            selectedPlan={props.subscription.selectedPlan}
            planPeriodType={props.subscription.planPeriodType}
          />
        )}
        {displayPlans
          .map((plan) => {
            const isFree = Number(plan.monthly_price) === 0;
            const displayPrice = getPlanDisplayPrice(plan, planPeriodType);
            const currentPeriod = normalizePeriod(
              userPlan.plan_period_type || userPlan.plan_type
            );
            const viewingPeriod = normalizePeriod(planPeriodType);
            const isCurrentPlan =
              String(plan.id) === String(userPlan.plan_id) &&
              (isFree || viewingPeriod === currentPeriod);
            const isScheduledPlan =
              String(plan.id) === String(userPlan.pending_plan_id) &&
              (!userPlan.pending_plan_type ||
                normalizePeriod(userPlan.pending_plan_type) === viewingPeriod);
            return (
              <Col lg="4" md="6" key={plan.id}>
                <div className="subscription_card">
                  <div className="cd_head">
                    <h3>
                      {plan.plan_name}{" "}
                      <label>
                        {planPeriodType === "monthly" ? (
                          <>(Monthly)</>
                        ) : (
                          <>(Annually)</>
                        )}
                      </label>
                      <ToolTip />
                    </h3>
                  </div>
                  <div className="cd_body">
                    <p className="subscription-card__desc">
                      Start automating basic scheduling features
                    </p>
                    <h2 className="subscription-card__price">
                      ${displayPrice}
                      {!isFree && <small>/mo / user</small>}
                    </h2>
                    {planPeriodType === "annual" && !isFree && (
                      <p className="subscription-card__desc mb-2">
                        Billed annually (${plan.annual_price}/yr)
                      </p>
                    )}

                    <div className="subscription-card__actions">
                      <PlanDetailModal
                        plans={plans}
                        userPlan={userPlan}
                        planPeriodType={planPeriodType}
                        onBuyPlan={onUpgradePlan}
                      />
                      {isCurrentPlan ? (
                        <button className="btn btn_success">
                          {isTrialingPlan(userPlan) ? "Trial active" : "Active"}
                        </button>
                      ) : isScheduledPlan ? (
                        <button className="btn btn_success">Scheduled</button>
                      ) : (
                        <>
                          {/* Already paid subscription */}
                          {userPlan.is_paid_subscription === "E" ? (
                            <>
                              <ConfirmPaymentModal
                                price={
                                  planPeriodType === "monthly"
                                    ? plan.monthly_price
                                    : plan.annual_price
                                }
                                plan_id={plan.id}
                                plan={plan}
                                planPeriodType={planPeriodType}
                                userCurrentPlan={userPlan}
                                upgradePlan={upgradePlanHandler}
                              />
                            </>
                          ) : (
                            <>
                              {/* Go to Payment Page */}
                              {
                                ((planPeriodType === "monthly" && plan.monthly_price > userPlan.monthly_price) ||
                                (planPeriodType === "annual" && plan.annual_price > userPlan.annual_price) ||
                                  isEmpty(userPlan.plan_id)) ? (
                                <button
                                  className="btn btn-outline brand"
                                  onClick={() => onUpgradePlan(plan)}
                                >
                                  {!isFree && isTrialEligible(userPlan)
                                    ? "Start 14-day trial"
                                    : "Upgrade Plan"}
                                </button>
                              ) : (
                                <button 
                                className="btn btn-outline brand"
                                onClick={() => onUpgradePlan(plan)}
                                >
                                  Downgrade Plan
                                </button>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Col>
            );
          })}
      </Row>
    </>
  );
};

const mapStateToProps = (state) => ({
  subscription: state.subscription,
});

export default connect(mapStateToProps, null)(SubscriptionPlans);
